import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useDropzone } from "react-dropzone";
import { ArrowLeftRight, FileText, TriangleAlert, X } from "lucide-react";
import validateDeleteAccount from "../../validation/delete-account";
import { getPreview, getTransferCandidates } from "../../utils/api/deletion";
import folderIcon from "../../assets/images/delete-modal-folder.svg";
import calendarIcon from "../../assets/images/delete-modal-calendar.svg";
import "./DeleteAccountModal.scss";

// Ownership can only move between people inside one account, so the panel is for
// sub-user deletions only. Sending transferTo on anything else is a 400.
const SUBUSER_TYPES = ["client_subuser", "admin_subuser"];

// Must mirror the server's whitelist exactly (INC-1955 §2): anything the browser lets
// through that the API rejects comes back as a 400 after the upload, which is a worse
// experience than refusing the file here.
const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024;

const ACCEPTED_ATTACHMENT_TYPES = {
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
  "application/pdf": [".pdf"],
  "application/msword": [".doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    ".docx",
  ],
  "message/rfc822": [".eml"],
  "application/vnd.ms-outlook": [".msg"],
};

const ATTACHMENT_ERRORS = {
  "file-too-large": "File size must be 10 MB or less.",
  "file-invalid-type":
    "Only PDF, PNG, JPG, DOC, DOCX, EML or MSG files are allowed.",
  "too-many-files": "Please upload only one file.",
};

const formatFileSize = (bytes) =>
  bytes < 1024 * 1024
    ? `${Math.ceil(bytes / 1024)} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

const initialForm = {
  requestedByName: "",
  requestedByEmail: "",
  requestedAt: null,
  reason: "",
  attachment: null,
};

const DeleteAccountModal = ({
  show,
  title = "Delete Account",
  accountName,
  // The person's own name, for the ownership line. accountName reads "Mitesh Pokar's
  // Account", which is right in the warning and wrong in "<name> owns 3 interviews".
  ownerName,
  // targetType/targetId drive the Interview Ownership panel. Left out, the modal behaves
  // exactly as before - every existing caller keeps working untouched.
  targetType,
  targetId,
  submitting,
  onHide,
  onSubmit,
}) => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [ownership, setOwnership] = useState({ counts: null, candidates: [] });
  const [transferOn, setTransferOn] = useState(false);
  const [transferTo, setTransferTo] = useState("");

  const isSubuser = SUBUSER_TYPES.includes(targetType);
  // Interview deletes read "Interview"; every other caller keeps "Account".
  const noun = title === "Delete Interview" ? "interview" : "account";

  useEffect(() => {
    if (!show || !isSubuser || !targetId) return;
    let active = true;
    // The counts and the dropdown come from two endpoints but belong to one panel, so a
    // failure on either just hides it rather than blocking the deletion.
    Promise.all([
      getPreview(targetType, targetId).catch(() => null),
      getTransferCandidates(targetId).catch(() => null),
    ]).then(([preview, transfer]) => {
      if (!active) return;
      const candidates = transfer?.candidates || [];
      setOwnership({ counts: preview?.cascade || null, candidates });
      // The parent account is the one entry guaranteed to exist, so it is the default.
      const owner = candidates.find((item) => item.isAccountOwner);
      setTransferTo(owner?._id || candidates[0]?._id || "");
    });
    return () => {
      active = false;
    };
  }, [show, isSubuser, targetType, targetId]);

  // Nothing to hand over means nothing to ask about (§4.3).
  const ownedInterviews = ownership.counts?.interviews || 0;
  const ownedPositions = ownership.counts?.positions || 0;
  // Candidates, question sets, applicants, workflows - the transfer moves these too, so a
  // sub-user who owns only them still needs the panel.
  const ownedOther = ownership.counts?.otherRecords || 0;
  const showOwnership =
    isSubuser &&
    (ownedInterviews > 0 || ownedPositions > 0 || ownedOther > 0) &&
    ownership.candidates.length > 0;

  // A client sub-user deleted with no recipient takes its interviews down with it, so the
  // checkbox is the difference between a handover and a deletion. Only client_subuser:
  // an admin sub-user's work is never cascaded. The other records are not deleted either
  // way, which is why this counts interviews alone.
  //
  // The delete takes only interviews that are not already deleted, which the backend counts as
  // liveInterviews. `interviews` is the ownership count and includes deleted ones, so warning
  // from it told a sub-user with 3 deleted interviews that 3 were about to be deleted. A backend
  // that predates the field leaves it undefined, and the ownership count stands in.
  const liveInterviews = ownership.counts?.liveInterviews ?? ownedInterviews;
  const willCascadeInterviews =
    targetType === "client_subuser" && !transferOn && liveInterviews > 0;

  const handleChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: ACCEPTED_ATTACHMENT_TYPES,
    maxSize: MAX_ATTACHMENT_SIZE,
    multiple: false,
    onDrop: (acceptedFiles, fileRejections) => {
      if (fileRejections.length) {
        const code = fileRejections[0].errors[0]?.code;
        setErrors((prev) => ({
          ...prev,
          attachment: ATTACHMENT_ERRORS[code] || "This file can't be uploaded.",
        }));
        return;
      }
      if (acceptedFiles[0]) handleChange("attachment", acceptedFiles[0]);
    },
  });

  const handleSubmit = () => {
    const { errors: formErrors, isValid } = validateDeleteAccount(form);
    setErrors(formErrors);
    if (isValid) {
      onSubmit({
        requestedByName: form.requestedByName.trim(),
        requestedByEmail: form.requestedByEmail.trim(),
        requestedAt: form.requestedAt,
        reason: form.reason.trim(),
        attachment: form.attachment,
        // Empty unless the panel is shown and the box is ticked: the transfer is a
        // choice, and the API rejects the field outright on a non-sub-user target.
        transferTo: showOwnership && transferOn ? transferTo : "",
      });
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      onExited={() => {
        setForm(initialForm);
        setErrors({});
      }}
      centered
      className="delete-account-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title className="f-bol">{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="delete-account-alert d-flex f-med">
          <TriangleAlert size={20} className="flex-shrink-0" />
          <span>
            You are about to delete <span className="f-bol">{accountName}</span>
            . Provide the request details below. Deleted records can be restored
            within 30 days.
          </span>
        </div>

        <div className="delete-account-field">
          <label className="f-sem-bol">
            Requested By <span className="required">*</span>
          </label>
          <div className="row g-3">
            <div className="col-sm-6">
              <input
                type="text"
                className={`delete-account-input f-med ${
                  errors.requestedByName ? "has-error" : ""
                }`}
                placeholder="Enter Name"
                value={form.requestedByName}
                onChange={(e) =>
                  handleChange("requestedByName", e.target.value)
                }
              />
              {errors.requestedByName && (
                <p className="delete-account-error">{errors.requestedByName}</p>
              )}
            </div>
            <div className="col-sm-6">
              <input
                type="email"
                className={`delete-account-input f-med ${
                  errors.requestedByEmail ? "has-error" : ""
                }`}
                placeholder="Enter Email"
                value={form.requestedByEmail}
                onChange={(e) =>
                  handleChange("requestedByEmail", e.target.value)
                }
              />
              {errors.requestedByEmail && (
                <p className="delete-account-error">
                  {errors.requestedByEmail}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-sm-6">
            <div className="delete-account-field">
              <label className="f-sem-bol">
                Requested At <span className="required">*</span>
              </label>
              <div className="delete-account-date">
                <DatePicker
                  selected={form.requestedAt}
                  onChange={(date) => handleChange("requestedAt", date)}
                  showTimeSelect
                  timeIntervals={15}
                  dateFormat="dd MMM yyyy, h:mm aa"
                  maxDate={new Date()}
                  filterTime={(time) => time <= new Date()}
                  placeholderText="Select date & time"
                  className={`delete-account-input f-med ${
                    errors.requestedAt ? "has-error" : ""
                  }`}
                />
                <img
                  src={calendarIcon}
                  alt=""
                  className="delete-account-date-icon"
                />
              </div>
              {errors.requestedAt && (
                <p className="delete-account-error">{errors.requestedAt}</p>
              )}
            </div>
          </div>
          <div className="col-sm-6">
            <div className="delete-account-field">
              <label className="f-sem-bol">
                Upload Supporting Doc <span className="required">*</span>
              </label>
              {form.attachment ? (
                <div className="delete-account-file d-flex align-items-center">
                  <FileText size={20} className="flex-shrink-0" />
                  <div className="delete-account-file-info">
                    <p className="delete-account-file-name f-sem-bol">
                      {form.attachment.name}
                    </p>
                    <p className="delete-account-file-size f-med">
                      {formatFileSize(form.attachment.size)}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="delete-account-file-remove"
                    aria-label="Remove file"
                    onClick={() => handleChange("attachment", null)}
                  >
                    <X size={18} />
                  </button>
                </div>
              ) : (
                <div
                  {...getRootProps({
                    className: `delete-account-dropzone d-flex align-items-center justify-content-between ${
                      isDragActive ? "is-active" : ""
                    } ${errors.attachment ? "has-error" : ""}`,
                  })}
                >
                  <input {...getInputProps()} />
                  <span className="f-med">Choose file</span>
                  <img
                    src={folderIcon}
                    alt=""
                    className="delete-account-dropzone-icon"
                  />
                </div>
              )}
              <p className="delete-account-dropzone-hint f-med">
                *supported PDF, PNG, JPG, DOC, DOCX, EML or MSG up to 10 MB max.
              </p>
              {errors.attachment && (
                <p className="delete-account-error">{errors.attachment}</p>
              )}
            </div>
          </div>
        </div>

        <div className="delete-account-field">
          <label className="f-sem-bol">
            Deletion Reason <span className="required">*</span>
          </label>
          <textarea
            rows={2}
            className={`delete-account-input delete-account-textarea f-med ${
              errors.reason ? "has-error" : ""
            }`}
            placeholder={`Describe why this ${noun} is being deleted`}
            value={form.reason}
            onChange={(e) => handleChange("reason", e.target.value)}
          />
          {errors.reason && (
            <p className="delete-account-error">{errors.reason}</p>
          )}
        </div>

        {/*
          [§4.3] Interview Ownership. For a client sub-user the checkbox below now decides
          between two different outcomes, so the panel has to say which one is selected:

            ticked   -> the work changes hands, nothing is deleted
            unticked -> the interviews are deleted with the person

          Positions and the other records are never deleted either way; without a recipient
          they are simply left on the deleted sub-user.
        */}
        {showOwnership && (
          <div className="delete-account-ownership">
            <div className="delete-account-ownership-title f-sem-bol d-flex align-items-center">
              <ArrowLeftRight size={16} className="flex-shrink-0" />
              <span>Interview Ownership</span>
            </div>
            <p className="delete-account-ownership-count f-med">
              {ownerName || accountName} owns{" "}
              <span className="f-sem-bol">{liveInterviews}</span>{" "}
              {liveInterviews === 1 ? "interview" : "interviews"}.
              {/* Interviews only, and only live ones: the same number the warning below and the
                  delete use, so the panel never reads "owns 125" next to "124 will be deleted".
                  Positions and other records still move with a transfer (and still open this
                  panel), but the line names the interview count alone. */}
            </p>
            {willCascadeInterviews && (
              <p className="delete-account-ownership-warning f-med">
                <strong>
                  {liveInterviews} {liveInterviews === 1 ? "interview" : "interviews"}{" "}
                  will be deleted
                </strong>{" "}
                along with this sub-user, because no one has been chosen to take them over.
                Tick the box below to hand them to another user instead.
              </p>
            )}
            <label className="delete-account-ownership-check f-med d-flex align-items-center">
              <input
                type="checkbox"
                checked={transferOn}
                onChange={(e) => setTransferOn(e.target.checked)}
              />
              <span>
                Transfer ownership to another active user. The handover applies
                immediately and is not undone by a restore.
              </span>
            </label>
            {transferOn && (
              <div className="delete-account-field mb-0">
                <label className="f-sem-bol">
                  Transfer To <span className="required">*</span>
                </label>
                <select
                  className="delete-account-input f-med"
                  value={transferTo}
                  onChange={(e) => setTransferTo(e.target.value)}
                >
                  {ownership.candidates.map((item) => (
                    <option key={item._id} value={item._id}>
                      {item.name}
                      {item.isAccountOwner ? " (account owner)" : ""}
                      {item.email ? ` - ${item.email}` : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          className="delete-account-btn delete-account-cancel-btn f-sem-bol"
          onClick={onHide}
        >
          Cancel
        </button>
        <button
          type="button"
          className="delete-account-btn delete-account-submit-btn f-sem-bol"
          disabled={submitting}
          onClick={handleSubmit}
        >
          {submitting
            ? "Deleting…"
            : noun === "interview"
              ? "Delete Interview"
              : "Delete Account"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default DeleteAccountModal;
